import { useCallback, useEffect, useRef, useState } from 'react'

import { CART_POLL_INTERVAL_MS, PROMOTION_LEVELS } from '../../lib/constants'

import type { CartMonitorState, CheckoutConfig, DiscountLevel } from '../../types'

/**
 * Read cart subtotal from window.checkoutConfig.totalsData.
 *
 * On cart page, Magento injects checkoutConfig with totalsData
 * containing subtotal_incl_tax (total with VAT in RON).
 */
function readCartTotal(): number {
  try {
    const config = (window as unknown as Record<string, unknown>).checkoutConfig as CheckoutConfig | undefined
    const totals = config?.totalsData
    if (!totals) return 0

    const amount = totals.subtotal_incl_tax ?? totals.grand_total ?? totals.subtotal
    if (amount === undefined || amount === null) return 0

    return typeof amount === 'number' ? amount : Number(amount) || 0
  } catch {
    return 0
  }
}

/**
 * Compute discount level from cart total.
 */
function computeLevel(total: number): DiscountLevel {
  return total >= PROMOTION_LEVELS[1] ? 2 : 1
}

/**
 * Hook that monitors cart total via Magento events + polling fallback.
 *
 * Primary: Listens for Magento's `customer-data-invalidate` and
 * `customEventReload` events for immediate cart updates.
 * Fallback: Polls window.checkoutConfig at intervals for environments
 * where events may not fire reliably.
 *
 * Returns current cart total and computed discount level.
 */
export function useCartMonitor(): CartMonitorState {
  const [state, setState] = useState<CartMonitorState>(() => {
    const total = readCartTotal()
    return { total, level: computeLevel(total) }
  })

  const lastTotal = useRef(state.total)

  const updateState = useCallback(() => {
    const total = readCartTotal()
    if (total !== lastTotal.current) {
      lastTotal.current = total
      setState({ total, level: computeLevel(total) })
    }
  }, [])

  useEffect(() => {
    // Read immediately on mount (checkoutConfig may populate after initial render)
    updateState()

    // Primary: Listen for Magento cart update events
    const handleCartEvent = () => {
      // Small delay to let Magento update checkoutConfig after event
      setTimeout(updateState, 100)
    }

    document.addEventListener('customer-data-invalidate', handleCartEvent)
    document.addEventListener('customEventReload', handleCartEvent)

    // Fallback: Poll for environments where events may not fire
    const intervalId = setInterval(updateState, CART_POLL_INTERVAL_MS)

    return () => {
      document.removeEventListener('customer-data-invalidate', handleCartEvent)
      document.removeEventListener('customEventReload', handleCartEvent)
      clearInterval(intervalId)
    }
  }, [updateState])

  return state
}
